import * as Yup from "yup";
import { centsToDollars, dollarsToCents } from "@/features/worksheets/money";
import { isoToMaskedDate, maskedDateToISO } from "@/lib/masks";
import { bool, num, str } from "../record";
import type { PortalRecord } from "../collection-card";

/** A single field in a spec-driven business form. */
export type FieldKind =
  | "text"
  | "number"
  | "money"
  | "select"
  | "checkbox"
  | "date"
  | "secret"; // masked PII (ssn/ein/account_no): write-only, never prefilled

export interface FieldSpec {
  name: string;
  label: string;
  kind?: FieldKind;
  options?: { value: string; label: string }[];
  required?: boolean;
}

export type FormValues = Record<string, string | boolean>;

/** Build a Formik initial-values object from a raw record for the given spec. */
export function initialFromRecord(record: PortalRecord | null, specs: FieldSpec[]): FormValues {
  const r = record ?? {};
  const out: FormValues = {};
  for (const f of specs) {
    switch (f.kind) {
      case "money":
        out[f.name] = centsToDollars(num(r, f.name) ?? 0);
        break;
      case "checkbox":
        out[f.name] = bool(r, f.name);
        break;
      case "date":
        out[f.name] = isoToMaskedDate(str(r, f.name));
        break;
      case "number":
        out[f.name] = num(r, f.name)?.toString() ?? "";
        break;
      case "secret":
        // Write-only: never echo the raw value back into the form.
        out[f.name] = "";
        break;
      default:
        out[f.name] = str(r, f.name);
    }
  }
  return out;
}

/** Convert Formik values into the server payload (snake_case, money→cents). */
export function payloadFromValues(
  values: FormValues,
  specs: FieldSpec[],
): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const f of specs) {
    const v = values[f.name];
    switch (f.kind) {
      case "money":
        out[f.name] = dollarsToCents(String(v ?? ""));
        break;
      case "checkbox":
        out[f.name] = Boolean(v);
        break;
      case "date":
        out[f.name] = v ? maskedDateToISO(String(v)) || null : null;
        break;
      case "number":
        out[f.name] = v === "" || v == null ? null : Number(v);
        break;
      case "secret": {
        // Only send a secret when the user typed one, so a blank edit keeps the
        // stored value instead of clearing it.
        const s = String(v ?? "").trim();
        if (s) out[f.name] = s;
        break;
      }
      default:
        out[f.name] = v;
    }
  }
  return out;
}

/** A lenient Yup schema: required text fields + non-negative money. */
export function schemaFor(specs: FieldSpec[]) {
  const shape: Record<string, Yup.AnySchema> = {};
  for (const f of specs) {
    if (f.kind === "money") {
      shape[f.name] = Yup.string().test(
        "non-negative",
        "Must be zero or more",
        (val) => !val || dollarsToCents(val) >= 0,
      );
    } else if (f.required) {
      shape[f.name] = Yup.string().trim().required("Required");
    }
  }
  return Yup.object(shape);
}
